/** Textele interfeței, într-un singur loc, în toate limbile în care există.
 *
 *  CE E AICI. Un dicționar per limbă (`ro.ts`, `en.ts`), o funcție care caută
 *  în el (`t`) și un provider care spune pe care limbă suntem. Nicio bibliotecă:
 *  tot ce ne trebuie sunt căutarea, interpolarea și pluralul, iar tipurile le
 *  face TypeScript mai bine decât orice configurație.
 *
 *  COMPLETITUDINEA E O CONDIȚIE DE COMPILARE. `ro.ts` definește mulțimea
 *  cheilor; orice alt catalog e tipizat `Catalog`, adică `Record<Cheie, string>`.
 *  O cheie nouă scrisă doar în română OPREȘTE build-ul până când primește textul
 *  englezesc — v. `docs/internationalizare.md`. Un test în plus
 *  (`i18n.test.ts`) prinde ce tipul nu poate: texte goale, plurale cu alt număr
 *  de forme, parametri care apar într-o limbă și lipsesc din alta.
 *
 *  DE CE ȘI UN `t` DE MODUL, NU DOAR HOOK-UL. Clientul de API (`api.ts`) nu e o
 *  componentă și nu poate chema un hook, dar exact acolo se nasc mesajele de
 *  eroare care ajung în toast. Limba curentă stă deci într-o variabilă de modul,
 *  ținută în pas cu starea React de `LimbaProvider` — o singură sursă, două
 *  porți de acces.
 *
 *  CHEILE SUNT NUME, NU TEXTE. `proiecte.gol.titlu`, nu «Niciun proiect încă».
 *  Cu textul pe post de cheie, orice corectură de virgulă în română ar șterge
 *  tăcut traducerea englezească.
 */
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import { bcp47, esteLimba, LIMBA_IMPLICITA, limbaDeStart, CHEIE_STOCARE, LIMBI } from "./limbi";
import type { Limba } from "./limbi";
import { ro } from "./ro";
import type { Catalog, Cheie } from "./ro";
import { en } from "./en";

export { LIMBI, LIMBA_IMPLICITA, esteLimba, bcp47 } from "./limbi";
export type { Limba } from "./limbi";
export type { Cheie } from "./ro";

const CATALOAGE: Record<Limba, Catalog> = { ro, en };

/** Valorile care intră în locurile goale ale textului: `{nume}`, `{n}`.
 *
 *  `null`/`undefined` sunt PRIMITE, nu respinse: o cifră care lipsește e o
 *  stare obișnuită într-un produs de calcul — un pas care n-a rulat, un câmp
 *  pe care documentul nu-l dă. Ele se scriu «—», semnul folosit peste tot
 *  altundeva pentru «nu se știe». Tipul strâns de dinainte cerea `String(x)`
 *  la fiecare chemare, iar acel `String(undefined)` scria «undefined» pe
 *  ecran — exact ce voia tipul să împiedice. */
export type Valoare = string | number | boolean | null | undefined;
export type Valori = Record<string, Valoare>;

/** Cheile cu plural. Convenția `#n` la coadă e ȘI marcajul pentru test: numai
 *  ele au voie să poarte trei forme despărțite de `|`. */
export type CheiePlural = Extract<Cheie, `${string}#n`>;

// Limba curentă, ca variabilă de modul — v. antetul. Se scrie DOAR din
// `seteazaLimba`, ca starea React și ea să nu poată diverge.
let limbaCurenta: Limba = LIMBA_IMPLICITA;
const abonati = new Set<(l: Limba) => void>();

export function limba(): Limba {
  return limbaCurenta;
}

export function seteazaLimba(noua: Limba) {
  if (noua === limbaCurenta) return;
  limbaCurenta = noua;
  // `lang` pe rădăcina documentului: după el se iau cititoarele de ecran,
  // corectorul ortografic al câmpurilor și despărțirea în silabe.
  document.documentElement.lang = noua;
  try {
    localStorage.setItem(CHEIE_STOCARE, noua);
  } catch {
    // Stocarea blocată costă doar limba ecranului de login la următoarea
    // vizită; profilul rămâne sursa adevărată.
  }
  abonati.forEach((f) => f(noua));
}

function umple(text: string, valori?: Valori): string {
  if (!valori) return text;
  return text.replace(/\{(\w+)\}/g, (intreg, nume: string) => {
    if (!(nume in valori)) return intreg;
    const v = valori[nume];
    return v == null ? "—" : String(v);
  });
}

/** E `x` o cheie a catalogului? Pentru cheile compuse la rulare dintr-un cod
 *  venit de la server (`etapa.<cod>`): un cod pe care ecranul nu-l știe nu
 *  devine o cheie crudă pe ecran, ci cade pe textul serverului. */
/** Textul unei chei în TOATE limbile, nu numai în cea curentă.
 *
 *  Pentru cine trebuie să RECUNOASCĂ un text, nu să-l scrie: documentele vechi
 *  poartă numele camerelor scrise de cititor în română, iar interfața trebuie
 *  să-și dea seama că «Dormitor» e chiar numele felului `bedroom` ca să-l poată
 *  spune englezește (v. `tronson/feluri::numeleCamerei`). */
export function inToateLimbile(cheie: Cheie): string[] {
  return LIMBI.map((l) => CATALOAGE[l.cod]?.[cheie]).filter(Boolean) as string[];
}

export function esteCheie(x: string): x is Cheie {
  return Object.prototype.hasOwnProperty.call(ro, x);
}

/** Textul cheii, în limba curentă.
 *
 *  Căderea pe română când textul lipsește e o plasă pentru producție, nu o cale
 *  de lucru: catalogul incomplet nu compilează, deci aici ajunge doar o limbă
 *  care a intrat în listă fără catalog. Mai bine un rând în română decât o
 *  cheie crudă pe ecranul cuiva.
 */
export function t(cheie: Cheie, valori?: Valori): string {
  const catalog = CATALOAGE[limbaCurenta] ?? ro;
  return umple(catalog[cheie] || ro[cheie] || cheie, valori);
}

/** Categoria de plural, după regulile CLDR ale limbii.
 *
 *  Româna are TREI forme, și de-aia pluralul nu se poate face cu un `n === 1`
 *  scris în componentă: «1 document», «3 documente», «20 DE documente». Engleza
 *  are două, deci formele 2 și 3 ale catalogului englezesc sunt identice — o
 *  repetiție care costă o linie și scutește o ramură în cod.
 */
export function categoriePlural(l: Limba, n: number): 0 | 1 | 2 {
  const x = Math.abs(n);
  if (l === "ro") {
    if (x === 1) return 0;
    // `few` în CLDR: 0, și orice număr al cărui rest la 100 cade în 1…19.
    const rest = x % 100;
    return x === 0 || (rest >= 1 && rest <= 19) ? 1 : 2;
  }
  return x === 1 ? 0 : 1;
}

/** Textul unei chei cu plural. Cele trei forme stau în aceeași valoare,
 *  despărțite de `|`: «un document|{n} documente|{n} de documente». */
export function tn(cheie: CheiePlural, n: number, valori?: Valori): string {
  const brut = CATALOAGE[limbaCurenta]?.[cheie] || ro[cheie];
  const forme = brut.split("|");
  const i = categoriePlural(limbaCurenta, n);
  return umple(forme[i] ?? forme[forme.length - 1] ?? "", { n, ...valori });
}

/** Marcajele recunoscute în catalog: îngroșare, italic și cod.
 *
 *  Accentul e parte din text, nu din aranjare: în altă limbă cade pe alt
 *  cuvânt și în altă poziție — «Ai drept de CITIRE pe proiectul ăsta». Deci
 *  stă în catalog, iar fraza rămâne întreagă. Cele trei marcaje spun lucruri
 *  diferite: `<b>` arată cuvântul-cheie, `<i>` nuanțează («geometria
 *  <i>precedentă</i>»), `<code>` e un simbol din formule (`<code>nu_d</code>`),
 *  care în orice limbă se scrie la fel și se citește cu font fix.
 */
const MARCAJ = /(<(?:b|i|code)>[\s\S]*?<\/(?:b|i|code)>)/g;

/** Textul, tăiat în bucăți marcate și nemarcate.
 *
 *  E public fiindcă unele pagini au nevoie să facă altceva cu bucata simplă —
 *  de pildă s-o treacă prin adnotarea de simboluri, ca fraza să aibă și
 *  îngroșări, și hover pe `d_re`. */
export function bucatiMarcate(
  text: string,
): { marcaj: "b" | "i" | "code" | null; text: string }[] {
  return text
    .split(MARCAJ)
    .filter((bucata) => bucata !== "")
    .map((bucata) => {
      const m = /^<(b|i|code)>([\s\S]*)<\/(?:b|i|code)>$/.exec(bucata);
      return m
        ? { marcaj: m[1] as "b" | "i" | "code", text: m[2] }
        : { marcaj: null, text: bucata };
    });
}

/** Textul, tăiat în noduri React. Se CONSTRUIESC elemente — nu se injectează
 *  HTML: un catalog nu e o sursă de încredere pentru `dangerouslySetInnerHTML`. */
function noduri(text: string): ReactNode[] {
  return bucatiMarcate(text).map((b, i) =>
    b.marcaj === "b" ? <b key={i}>{b.text}</b>
      : b.marcaj === "i" ? <i key={i}>{b.text}</i>
      : b.marcaj === "code" ? <code key={i}>{b.text}</code>
      : <span key={i}>{b.text}</span>);
}

/** Textul cheii cu marcajele lui, ca noduri React. */
export function tx(cheie: Cheie, valori?: Valori): ReactNode[] {
  return noduri(t(cheie, valori));
}

/** Textul unei chei cu plural, cu marcajele lui.
 *
 *  Există fiindcă unele fraze numără ȘI accentuează: «în afară de <b>3</b>
 *  gropi». Fără ea, componenta ar alege între cifra îngroșată și forma corectă
 *  de plural — iar alegerea aia n-are răspuns bun.
 */
export function txn(cheie: CheiePlural, n: number, valori?: Valori): ReactNode[] {
  return noduri(tn(cheie, n, valori));
}

// --- formatarea cifrelor și a datelor -----------------------------------

/** Un număr, în convenția limbii: 12,5 în română, 12.5 în engleză.
 *
 *  Contează mai mult decât pare într-un produs de calcul: aceeași cifră scrisă
 *  cu punct și cu virgulă e citită ca două valori diferite de un om obișnuit cu
 *  cealaltă convenție.
 */
export function numar(v: number | null | undefined,
                      optiuni: Intl.NumberFormatOptions = {}): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toLocaleString(bcp47(limbaCurenta), optiuni);
}

export function data(iso: string | null | undefined,
                     optiuni: Intl.DateTimeFormatOptions = {}): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(bcp47(limbaCurenta), optiuni);
}

export function ora(iso: string | Date | null | undefined,
                    optiuni: Intl.DateTimeFormatOptions =
                      { hour: "2-digit", minute: "2-digit" }): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString(bcp47(limbaCurenta), optiuni);
}

export function dataOra(iso: string | Date | null | undefined,
                        optiuni: Intl.DateTimeFormatOptions =
                          { dateStyle: "short", timeStyle: "short" }): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(bcp47(limbaCurenta), optiuni);
}

// --- partea de React ----------------------------------------------------

const Context = createContext<Limba>(LIMBA_IMPLICITA);

/** Limba, o singură dată pentru toată aplicația.
 *
 *  Sursa e PROFILUL: `user.language`, cerut o dată cu sesiunea. Cât timp ea nu
 *  a răspuns — și pe ecranul de login, unde nici nu există un profil — se
 *  folosește ultima limbă de pe aparat, altfel cea a browserului
 *  (`limbaDeStart`).
 */
export function LimbaProvider({ limbaProfilului, children }:
                              { limbaProfilului?: string | null;
                                children: ReactNode }) {
  const [activa, setActiva] = useState<Limba>(() => {
    const pornire = limbaDeStart();
    seteazaLimba(pornire);
    return pornire;
  });

  // Un singur abonament la variabila de modul: și un `seteazaLimba` chemat din
  // afara React (dintr-un test, dintr-un răspuns de API) trebuie să redeseneze.
  useEffect(() => {
    abonati.add(setActiva);
    return () => { abonati.delete(setActiva); };
  }, []);

  useEffect(() => {
    if (esteLimba(limbaProfilului)) seteazaLimba(limbaProfilului);
  }, [limbaProfilului]);

  return <Context.Provider value={activa}>{children}</Context.Provider>;
}

/** Traducătorul. `const t = useT()` în fiecare componentă care scrie text.
 *
 *  Funcția se reface la schimbarea limbii — asta e ce redesenează ecranul: o
 *  identitate stabilă ar lăsa componentele memoizate cu textul vechi.
 */
export function useT() {
  // Limba intră în dependențe ca funcția să fie ALTA după schimbare: cu o
  // identitate stabilă, componentele memoizate ar rămâne cu textul vechi.
  const l = useContext(Context);
  return useMemo(() => (cheie: Cheie, valori?: Valori) => t(cheie, valori), [l]);
}

/** Traducătorul care păstrează îngroșările din text. */
export function useTx() {
  const l = useContext(Context);
  return useMemo(() => (cheie: Cheie, valori?: Valori) => tx(cheie, valori), [l]);
}

/** Traducătorul cu plural, pentru rândurile care numără. */
export function useTn() {
  const l = useContext(Context);
  return useMemo(
    () => (cheie: CheiePlural, n: number, valori?: Valori) => tn(cheie, n, valori),
    [l]);
}

/** Traducătorul cu plural care păstrează îngroșările. */
export function useTxn() {
  const l = useContext(Context);
  return useMemo(
    () => (cheie: CheiePlural, n: number, valori?: Valori) => txn(cheie, n, valori),
    [l]);
}

/** Limba activă și cum se schimbă — pentru selectorul din profil. */
export function useLimba(): Limba {
  return useContext(Context);
}

/** Formatarea cifrelor și a datelor, legată de limba activă.
 *
 *  Separată de `useT` fiindcă o componentă poate scrie numai cifre: fără un
 *  abonament propriu la context, ea n-ar fi redesenată la schimbarea limbii și
 *  ar rămâne cu virgula veche lângă textele noi.
 */
export function useFormat() {
  const l = useContext(Context);
  return useMemo(() => ({ numar, data, ora, dataOra, limba: l }), [l]);
}
